import { useEffect, useState } from 'react';
import { Mic } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import VoiceStudioOverlay from '@/components/sections/VoiceStudioOverlay';

const DESTINATIONS = {
  home: '/',
  studio: '/#studio',
  studio_standard: '/#studio',
  studio_pro: '/#studio',
  dashboard: '/dashboard',
  projects: '/projects',
  solutions: '/solutions',
  augmented_reality: '/ar',
  companies: '/companies',
  training: '/training',
  scanner: '/scanner',
  investor: '/investor',
  acquisition: '/acquire',
  rnd_dashboard: '/rnd-dashboard',
  patent_mapping: '/patent-mapping',
  integrations: '/integrations',
  pricing: '/pricing',
  marketplace: '/marketplace',
  licensing: '/licensing',
  engine_compose: '/compose',
  engines: '/engines',
  trust: '/trust',
  about: '/about',
  contact: '/contact',
  faq: '/faq',
  blog: '/blog',
};

const handoffKey = 'nexus-voice-design-request';

export default function GlobalNexusVoiceAssistant() {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [preferredMode, setPreferredMode] = useState('standard');

  useEffect(() => {
    const openVoice = (event) => {
      setPreferredMode(event.detail?.studioMode === 'pro' ? 'pro' : 'standard');
      setOpen(true);
    };
    window.addEventListener('nexus:open-voice', openVoice);
    return () => window.removeEventListener('nexus:open-voice', openVoice);
  }, []);

  const routeDesignRequest = async (prompt, options = {}) => {
    const cleanPrompt = String(prompt || '').trim();
    if (!cleanPrompt) return;
    const payload = {
      id: globalThis.crypto?.randomUUID?.() || `voice-${Date.now()}`,
      prompt: cleanPrompt,
      mode: options.mode || 'create',
      studioMode: options.studioMode || preferredMode,
      createdAt: Date.now(),
    };

    // Hiding the surface does not unmount this global component, so a live
    // conversation continues while the generated design becomes visible.
    setOpen(false);
    sessionStorage.setItem(handoffKey, JSON.stringify(payload));
    if (location.pathname === '/') {
      if (window.location.hash !== '#studio') window.history.replaceState(null, '', '/#studio');
      window.dispatchEvent(new CustomEvent('nexus:voice-design-request', { detail: payload }));
      window.setTimeout(() => document.getElementById('studio')?.scrollIntoView({ behavior: 'smooth' }), 50);
      return;
    }

    navigate('/#studio');
  };

  const navigatePlatform = async (destination) => {
    const target = DESTINATIONS[destination];
    if (!target) throw new Error(`Unknown Nexus destination: ${destination}`);
    const requestedMode = destination === 'studio_pro'
      ? 'pro'
      : (destination === 'studio_standard' ? 'standard' : null);
    if (requestedMode) {
      setPreferredMode(requestedMode);
      sessionStorage.setItem('nexus-studio-mode', requestedMode);
    }
    setOpen(false);
    navigate(target);
    if (target.includes('#studio')) {
      window.setTimeout(() => {
        if (requestedMode) {
          window.dispatchEvent(new CustomEvent('nexus:set-studio-mode', { detail: { studioMode: requestedMode } }));
        }
        document.getElementById('studio')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const inspectCurrentPage = async () => {
    const main = document.querySelector('main') || document.body;
    const visibleText = String(main?.innerText || '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
      .slice(0, 12000);
    return {
      path: `${location.pathname}${location.hash || ''}`,
      title: document.title,
      visible_content: visibleText || (language === 'tr' ? 'Görünür içerik bulunamadı.' : 'No visible content found.'),
      note: language === 'tr'
        ? 'Yalnızca kullanıcının tarayıcıda görebildiği içerik aktarılmıştır.'
        : 'Only content visible in the user’s browser was provided.',
    };
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={language === 'tr' ? 'Nexus canlı sesli asistanı aç' : 'Open Nexus live voice assistant'}
        title={language === 'tr' ? 'Nexus canlı asistan' : 'Nexus live assistant'}
        className={`fixed bottom-24 left-4 z-[190] grid h-14 w-14 place-items-center rounded-full border border-cyan-300/40 bg-[#071827] text-cyan-200 shadow-[0_0_35px_-8px_rgba(34,211,238,0.8)] transition hover:scale-105 hover:bg-cyan-400 hover:text-[#061016] md:bottom-6 md:left-6 ${open ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
        data-testid="global-nexus-voice-button"
      >
        <Mic className="h-6 w-6" />
      </button>
      <VoiceStudioOverlay
        open={open}
        language={language}
        onClose={() => setOpen(false)}
        onConfirm={(text) => { void routeDesignRequest(text, { mode: 'create' }); }}
        onDesignRequest={routeDesignRequest}
        onNavigate={navigatePlatform}
        onInspectPage={inspectCurrentPage}
      />
    </>
  );
}
